import styled from "styled-components"

import { BREAKPOINTS, MEDIA_QUERIES } from "@constants"

type HEIGHTS = { 
  MOBILE: number,
  TABLET_INTERVIEW?: number,
  TABLET_LIBRARY?: number,
  DESKTOP: number,
}

export const HEADER_HEIGHTS: HEIGHTS = {
  MOBILE: 52,
  TABLET_INTERVIEW: 102,
  TABLET_LIBRARY: 75,
  DESKTOP: 95,
}

const FILTER_DRAWER_HEIGHTS: HEIGHTS = {
  MOBILE: 86,
  DESKTOP: 109,
}

const ACTION_BUTTONS_HEIGHTS: HEIGHTS = {
  MOBILE: 62,
  DESKTOP: 87,
}

export const QuestionListLibraryDragHelperDIV = styled.div`
  opacity: 0;
  pointer-events: none;
  width: calc(100% - var(--spacing--S) * 2);
  height: calc(100% - ${HEADER_HEIGHTS.TABLET_LIBRARY}px);
  position: absolute;
  top: 95px;
  left: var(--spacing--S);
  z-index: 3;
  padding: 0 var(--spacing--S) var(--spacing--S);
  background: #ECF1FF; // ?? color of Interview bg
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: opacity 0.3s, background 0.3s;

  .is-INTERVIEW & {
    height: calc(100% - ${HEADER_HEIGHTS.TABLET_INTERVIEW}px);
  }

  @media (min-width: 1020px) {
    .is-INTERVIEW &,
    & {
      height: calc(100% - ${HEADER_HEIGHTS.DESKTOP}px);
    }
  }

  .gatsby-image-wrapper {
    margin: 0 0 var(--spacing--XS);
  }

  p {
    font-size: var(--font-size--M);
    color: var(--color--charcoal-1100);
    max-width: 276px;
    margin: 0 auto;
  }

  &#add-drop-zone {
    opacity: 1;
    pointer-events: all;
    top: calc(95px + 24px + var(--spacing--XS)); // account for the Job Title field height
    height: calc(100% - 95px - 24px - var(--spacing--XS)); // account for the Job Title field height
    background: transparent;

    > * {
      position: relative;
      z-index: 1;
    }

    &::before {
      content: "";
      display: block;
      position: absolute;
      top: var(--spacing--XS);
      left: 0;
      z-index: 0;
      width: 100%;
      height: calc(100% - var(--spacing--XS));
      background: var(--color--cobalt-100);
      border-radius: 6px 6px 0 0;
      opacity: 0;
      transition: opacity 0.3s;
    }

    &.is-dragging:hover::before {
      opacity: 0.6;
    }
  }
`

export const QuestionListSECTION = styled.section`
  --window-height: 100vh;

  position: relative;
  background-color: var(--color--clay-200);
  border: 1px solid var(--color--charcoal-300);
  border-radius: 6px 6px 0 0;
  margin-bottom: var(--spacing--S);

  &.is-INTERVIEW {
    height: calc(var(--window-height) - var(--spacing--S) * 3 - ${HEADER_HEIGHTS.MOBILE}px - 43px - 72px); // 43px = .Tabs height, 72px = footer height
  }

  &.is-LIBRARY {
    height: calc(var(--window-height) - var(--spacing--S) * 3 - ${HEADER_HEIGHTS.MOBILE}px - ${ACTION_BUTTONS_HEIGHTS.MOBILE}px - 43px - 72px); // 43px = .Tabs height, 72px = footer height
    background: var(--color--grey-300);
  }

  // Category heading... only rendered on Library/Desktop
  .category-heading {
    display: flex;
    position: relative;
    font-size: var(--font-size--XS);
    font-weight: var(--font-weight--medium);
    color: var(--color--charcoal-600);
    margin-bottom: 10px;

    span {
      position: relative;
      z-index: 1;
      display: block;
      width: fit-content;
      padding-right: 10px;
      background: var(--color--clay-200);
    }

    &::before {
      content: "";
      display: block;
      width: 100%;
      height: 1px;
      position: absolute;
      top: calc(50% + 1px);
      left: 0;
      z-index: 0;
      background: var(--color--charcoal-500);
    }
  }

  // Expand/collapse styles!
  @media (max-width: ${BREAKPOINTS.M}px) {
    transition: height 0.3s cubic-bezier(0.99, 0.16, 0, 0), padding 0.3s;
    overflow: hidden;

    &.is-collapsed {

      &.is-LIBRARY,
      &.is-INTERVIEW {
        height: ${HEADER_HEIGHTS.MOBILE}px;
      }

      &.is-INTERVIEW {
        height: ${HEADER_HEIGHTS.MOBILE + ACTION_BUTTONS_HEIGHTS.MOBILE}px;
      }
    }
  }

  ${MEDIA_QUERIES.M} {
    margin-bottom: 0;
    overflow: visible;

    &.is-LIBRARY,
    &.is-INTERVIEW {
      // height is calc(var(--window-height) - margins/padding_height - tabs_height - footer_height)
      height: calc(var(--window-height) - var(--spacing--S) - 43px - 72px);
    }

    // show the "remove" or "add" drag helper box on touch devices when a card is being dragged over the Library/Interview column
    &:hover ${QuestionListLibraryDragHelperDIV} {
      opacity: 1;
      pointer-events: all;
    }
  }

  ${MEDIA_QUERIES.L} {
    &.is-LIBRARY,
    &.is-INTERVIEW {
      // height is calc(var(--window-height) - margins/padding_height - tabs_height - footer_height)
      height: calc(var(--window-height) - var(--spacing--S) - 54px - 72px);
    }
  }

  ${MEDIA_QUERIES.XL} {
    &.is-LIBRARY,
    &.is-INTERVIEW {
      // height is calc(var(--window-height) - margins/padding_height - tabs_height - footer_height)
      height: calc(var(--window-height) - var(--spacing--S) - 64px - 72px);
    }
  }

  // Always show the "remove" or "add" drag helper box on touch devices when a card is being dragged on touch devices
  ${MEDIA_QUERIES.TOUCH} {
    ${QuestionListLibraryDragHelperDIV} {
      opacity: 1;
      pointer-events: all;
    }
  }
`

export const QuestionListHEADER = styled.header`
  background-color: var(--color--clay-200);
  border-radius: 6px 6px 0 0;
  height: ${HEADER_HEIGHTS.MOBILE}px;
  left: 0;
  overflow: hidden;
  padding: 20px var(--spacing--S) 10px;
  position: absolute;
  top: 0;
  transition: box-shadow 0.3s linear;
  width: 100%;
  z-index: 2;

  .is-scrolled & {
    box-shadow: 0 0 1px 0 var(--color--charcoal-500);
  }

  &::before {
    background: var(--color--action-default);
    content: "";
    display: block;
    height: 8px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .is-LIBRARY & {
    transition: height 0.3s, padding 0.3s, box-shadow 0.3s linear;

    &::before {
      background: var(--color--yellow-400);
    }

    .is-filter-drawer-open& {
      height: ${HEADER_HEIGHTS.MOBILE + FILTER_DRAWER_HEIGHTS.MOBILE}px;
    }

    ${MEDIA_QUERIES.M} {
      .is-filter-drawer-open& {
        height: ${HEADER_HEIGHTS.DESKTOP + FILTER_DRAWER_HEIGHTS.DESKTOP - 24}px; // -24px because the 24px of padding disappears
        padding-bottom: 0;
      }
    }
  }

  .is-INTERVIEW & {
    height: ${HEADER_HEIGHTS.MOBILE}px;
  }

  h3 {
    margin: 0;
  }

  ${MEDIA_QUERIES.M} {
    height: ${HEADER_HEIGHTS.TABLET_LIBRARY}px; // minus padding height is 32px
    padding-top: 20px;
    padding-bottom: 24px;

    .is-INTERVIEW & {
      height: ${HEADER_HEIGHTS.TABLET_INTERVIEW}px;
      padding-bottom: 10px;
    }

    h3 {
      line-height: 32px;
      font-weight: var(--font-weight--semibold);
    }
  }

  ${MEDIA_QUERIES.L} {
    padding-top: 40px;
    height: ${HEADER_HEIGHTS.DESKTOP}px; // minus padding height is 32px

    .is-INTERVIEW & {
      height: ${HEADER_HEIGHTS.DESKTOP}px;
    }
  }
`

export const QuestionListActionsDIV = styled.div`
  display: flex;
  position: absolute;
  right: 0;
  top: 0;
  padding-top: 8px;
  height: ${HEADER_HEIGHTS.MOBILE}px;
  line-height: ${HEADER_HEIGHTS.MOBILE}px;

  .is-LIBRARY & {
    height: ${HEADER_HEIGHTS.DESKTOP}px;
    line-height: ${HEADER_HEIGHTS.DESKTOP}px;
  }

  ${MEDIA_QUERIES.M} {
    .is-INTERVIEW & {
      position: static;
      padding-top: 8px;
      height: 40px;
    }
  }

  @media (min-width: 1020px) {
    .is-LIBRARY &,
    .is-INTERVIEW & {
      position: absolute;
      padding-top: 8px;
      height: ${HEADER_HEIGHTS.DESKTOP}px;
      line-height: ${HEADER_HEIGHTS.DESKTOP}px;
    }
  }

  button {
    width: ${HEADER_HEIGHTS.MOBILE - 10}px;
    height: ${HEADER_HEIGHTS.MOBILE - 10}px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    .Icon {
      position: relative;
    }
    
    .Icon,
    &:active .Icon,
    &:hover .Icon,
    &:focus .Icon {
      background: var(--color--charcoal-700);
      left: 0;
      position: absolute;
      top: 0;
    }

    &.filter-toggle-button {
      position: relative;

      .Icon {
        position: relative;
        z-index: 1;
      }
    }

    &.collapse-button {
      transform: rotate(90deg);

      .is-collapsed & {
        transform: rotate(-90deg);
      }
    }

    ${MEDIA_QUERIES.M} {
      width: auto;
      padding: 0 var(--spacing--S);
      height: ${HEADER_HEIGHTS.TABLET_LIBRARY || 0 - 10}px;
    }

    @media (min-width: 1020px) {
      width: auto;
      padding: 0 var(--spacing--S);
      height: ${HEADER_HEIGHTS.DESKTOP - 10}px;
    }

    // Style hover effects on non-touch devices for non-.Button buttons
    ${MEDIA_QUERIES.MOUSE} {
      &:not(.Button) {
        position: relative;

        &::before {
          content: "";
          display: block;
          width: 30px;
          height: 30px;
          background: transparent;
          position: absolute;
          top: 50%;
          left: 50%;
          z-index: 0;
          transform: translate(-50%, -50%);
          border-radius: 3.5px;
          transition: background 0.3s;
        }

        &:hover::before {
          background: var(--color--cobalt-100) !important; // !important to override filter styles
        }
      }
    }
  }
`

export const QuestionListFilterDrawerDIV = styled.div`
  height: ${FILTER_DRAWER_HEIGHTS.MOBILE}px;
  padding: 16px 0 0;
  opacity: 0;
  transition: opacity 0.3s;

  .is-filter-drawer-open & {
    opacity: 1;
  }

  p {
    font-size: var(--font-size--XS);
    font-weight: var(--font-weight--medium);
    color: var(--color--charcoal-800);
    margin-bottom: 10px;
  }

  div {
    display: flex;
    flex-wrap: none;
    overflow: auto;
    padding-bottom: 10px;
  }

  button {
    display: inline-block;
    background: var(--color--clay-300);
    border-radius: 4px;
    color: var(--color--charcoal-1000 );
    font-size: var(--font-size--S);
    font-weight: 500;
    height: var(--spacing--M);
    line-height: 25px;
    margin-right: 8px;
    padding: 0 8px;

    &:last-child {
      margin-right: 0;
    }

    ${MEDIA_QUERIES.M} {
      margin-right: 10px;
    }

    &.is-current {
      background: var(--color--clay-500);;
    }
  }

  ${MEDIA_QUERIES.M} {
    height: ${FILTER_DRAWER_HEIGHTS.DESKTOP}px;
    padding: 24px 0 0;

    div {
      padding: 0 0 23px;
    }
  }
`

export const QuestionListScrollWrapperDIV = styled.div`
  padding: ${HEADER_HEIGHTS.MOBILE}px 0 0;
  height: 100%;
  background-color: var(--color--clay-200);

  .is-LIBRARY & {
    border-radius: 0 0 6px 6px;
    transition: padding 0.3s;

    .is-filter-drawer-open& {
      padding-top: ${HEADER_HEIGHTS.MOBILE + FILTER_DRAWER_HEIGHTS.MOBILE}px;
    }
  }
  
  .is-INTERVIEW & {
    padding-top: ${HEADER_HEIGHTS.MOBILE}px;
    padding-bottom: 62px;

    .is-collapsed& {
      margin-bottom: 0;
    }
  }

  ${MEDIA_QUERIES.M} {
    .is-LIBRARY & {
      padding-top: ${HEADER_HEIGHTS.TABLET_LIBRARY}px;
      border-radius: 0;

      .is-filter-drawer-open& {
        padding-top: ${HEADER_HEIGHTS.DESKTOP + FILTER_DRAWER_HEIGHTS.DESKTOP}px;
      }
    }

    .is-INTERVIEW & {
      padding-top: ${HEADER_HEIGHTS.TABLET_INTERVIEW}px;
      padding-bottom: 0;
      margin-bottom: 0;
    }

    .is-filter-drawer-open & {
      padding-top: ${HEADER_HEIGHTS.TABLET_LIBRARY + FILTER_DRAWER_HEIGHTS.DESKTOP - 24}px; // -24px because the 24px of padding disappears
    }
  }

  @media (min-width: 1020px) {
    .is-LIBRARY & {
      padding-top: ${HEADER_HEIGHTS.DESKTOP}px;

      .is-filter-drawer-open& {
        padding-top: ${HEADER_HEIGHTS.DESKTOP + FILTER_DRAWER_HEIGHTS.DESKTOP - 24}px; // -24px because the 24px of padding disappears
      }
    }

    .is-INTERVIEW & {
      padding-top: ${HEADER_HEIGHTS.DESKTOP}px;
    }
  }
`

export const QuestionListScrollerDIV = styled.div`
  height: 100%;
  overflow: auto;

  ${MEDIA_QUERIES.M} {
    padding: 0 var(--spacing--S);
  }

  // If on a touch device, lock the scroll bar when a question is dragging.
  ${MEDIA_QUERIES.TOUCH} {
    .is-dragging-question & {
      overflow: hidden;
    }
  }
`

